import { REGION_LABELS } from './regions.js';

/**
 * WHICH PUBLIC HOLIDAYS ARE **THE COMPANY'S OWN** — the headline figure.
 *
 * ⚠️ THIS EXISTS BECAUSE THE ANSWER GOT WRITTEN A FOURTH TIME AND WAS WRONG AGAIN.
 * Rob, 07/08/2026: the Leave Policy panel said *"30 days + 44 bank holidays = 74 total
 * days off"* for a UK company. There is no country with 44 bank holidays. It was
 * counting the UK's 9, France's 11, Algeria's 14 and Saudi Arabia's 13 together, minus
 * the dates they happen to share.
 *
 * The filter it used asked only about REGION — England & Wales, Scotland, Northern
 * Ireland. It never asked about COUNTRY. And non-UK holidays are stored with **no
 * region at all** (decided 06/08/2026, F54: a region is a UK sub-division and means
 * nothing in Algeria), so every foreign holiday passed a test that was only looking for
 * "not Scotland".
 *
 * ⚠️ THE SAME FAULT AS F51 AND F52, IN A THIRD PLACE. Both were checks that knew about
 * regions and were blind to countries. The rule now lives here, once, with checks
 * beside it — `node src/lib/companyHolidays.checks.mjs`.
 *
 * ⚠️ THIS MIRRORS `backend/services/holidays_util.holiday_applies` DELIBERATELY, AND
 * MUST KEEP MIRRORING IT. The backend decides what people actually get; this decides
 * what the admin is told they get. If the two drift, the screen lies about the maths.
 * Every rule below is in the Python too, for the reason recorded there.
 */

/**
 * Does one holiday row apply to somebody in this country and region?
 *
 * ⚠️ COUNTRY IS CHECKED ONLY WHEN BOTH SIDES HAVE ONE. Rows from the UK-only era carry
 * no country, and a company with no country set must keep seeing them: a filter that
 * silently hid every holiday would be far worse than one that shows a few too many.
 *
 * ⚠️ NO REGION MEANS "EVERYWHERE IN THIS COUNTRY" — that is how a company-wide day and
 * a country without sub-divisions are both stored. And **absent means falsy, not null**:
 * `''` and `'   '` mean exactly what `null` means here. Treating `''` as a real region
 * cost Algeria every one of its holidays once already.
 */
export const holidayApplies = (holiday, countryCode, region) => {
  if (!holiday) return false;
  const rowCountry = (holiday.country_code || '').trim();
  const wantCountry = (countryCode || '').trim();
  if (rowCountry && wantCountry
      && rowCountry.toUpperCase() !== wantCountry.toUpperCase()) {
    return false;
  }
  const rowRegion = (holiday.region || '').trim();
  return !rowRegion || rowRegion === (region || '').trim();
};

/**
 * The company's own bank holidays for the year, as DISTINCT DATES.
 *
 * ⚠️ DE-DUPLICATING BY DATE IS ONLY SAFE **AFTER** FILTERING, and that order is the
 * whole point. England & Wales, Scotland and Northern Ireland each have their own
 * 1 January row, and so do Algeria and France; collapsing on date first would delete
 * real holidays for real people. `scripts/dedupe_public_holidays.py` warns about the
 * same trap in capitals.
 *
 * ⚠️ IT IS A HEADLINE, NOT THE WHOLE TRUTH, and the screen says so. A company with
 * offices in four countries has four different answers; this one is the head office's.
 * Nobody's leave is calculated from it — the backend resolves each person's own
 * country and region for that.
 */
export const companyBankHolidays = (rows, countryCode, region) => {
  const seen = new Set();
  return (rows || []).filter((h) => {
    if (!holidayApplies(h, countryCode, region)) return false;
    if (seen.has(h.date)) return false;
    seen.add(h.date);
    return true;
  });
};

/** Short label for the country/region a headline figure belongs to. */
// ⚠️ ONE REGION VOCABULARY — `lib/regions.js`. There were four of these on 13/08/2026
// and two of them spelled England & Wales differently.

/**
 * "England & Wales" / "your head office" — what the headline is FOR.
 *
 * ⚠️ NEVER RETURNS AN EMPTY STRING. A blank here would render as "bank holidays for ."
 * which reads like a rendering fault; "your head office" is always true.
 */
export const headlineScopeLabel = (countryName, region) =>
  REGION_LABELS[(region || '').trim()] || (countryName || '').trim() || 'your head office';

/**
 * Which OTHER countries this company has holidays for. Sorted, distinct, no blanks.
 *
 * ⚠️ IT ANSWERS "SHOULD WE ADMIT THIS IS ONLY THE HEADLINE?" — Rob's option A,
 * 07/08/2026. A single-country company is told a plain figure; a company with offices
 * abroad is told whose figure it is and that the others differ. Saying that
 * unconditionally would put a confusing caveat in front of every UK-only customer, and
 * they are most of them.
 *
 * ⚠️ IT READS THE HOLIDAY ROWS, NOT THE LOCATIONS. The company list endpoint already
 * returns a row for every country the company has an office in, so the answer is
 * sitting in data this screen has fetched — no second request, and no second opinion
 * about where the company operates.
 */
export const otherCountryCodes = (rows, countryCode) => {
  const want = (countryCode || '').trim().toUpperCase();
  const found = new Set();
  (rows || []).forEach((h) => {
    const code = (h.country_code || '').trim().toUpperCase();
    if (code && code !== want) found.add(code);
  });
  return [...found].sort();
};
